import React from 'react';
import { ErrorBoundary as SentryErrorBoundary } from '@sentry/react';
import { useLocation } from 'react-router-dom';
import { useErrorContext } from '../contexts/ErrorContext';
import { DefaultError } from './default-error';

/**
 * Extracts the component name from React error component stack
 * Looks for the first meaningful component name after common React internals
 */
const extractComponentName = (componentStack) => {
  if (!componentStack) return 'Unknown';

  // Split by newlines and filter out React internals and empty lines
  const lines = componentStack.split('\n').filter(line => line.trim());

  // Skip common React internals
  const skipPatterns = ['ErrorBoundary', 'RouteContext', 'Router', 'Routes', 'Route'];

  for (const line of lines) {
    // Extract component name from patterns like "at ComponentName" or "in ComponentName"
    const match = line.match(/(?:at|in)\s+([A-Z][A-Za-z0-9_]*)/);
    if (match && !skipPatterns.includes(match[1])) {
      return match[1];
    }
  }

  return 'Unknown';
};

/**
 * Extracts page name from route path
 * Examples: "/transactions" => "transactions", "/vendors/123" => "vendors"
 */
const extractPageFromPath = (pathname) => {
  if (!pathname || pathname === '/') return 'home';

  const segments = pathname.split('/').filter(Boolean);
  return segments[0] || 'unknown';
};

/**
 * Enhanced ErrorBoundary component that automatically infers scope and tags
 *
 * Props:
 * - fallback: Component or element to show on error (default: DefaultError)
 * - showFallback: Whether to show fallback UI (default: true, set to false for silent errors)
 * - additionalTags: Optional object with additional tags to set
 * - children: Components to wrap
 */
const EnhancedErrorBoundaryInner = ({
  fallback = DefaultError,
  showFallback = true,
  additionalTags = {},
  children
}) => {
  const location = useLocation();
  const { getErrorContext } = useErrorContext();

  const beforeCapture = (scope, error, errorInfo) => {
    // 1. Extract component name from error stack
    const componentName = extractComponentName(errorInfo?.componentStack);
    scope.setTag('component', componentName);

    // 2. Add page/route context
    const page = extractPageFromPath(location?.pathname);
    scope.setTag('page', page);
    scope.setTag('route', location?.pathname || 'unknown');

    // 3. Add any context set by components via ErrorContext
    const contextMetadata = getErrorContext();
    Object.entries(contextMetadata).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        scope.setTag(key, String(value));
      }
    });

    // 4. Add any additional tags passed as props
    Object.entries(additionalTags).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        scope.setTag(key, String(value));
      }
    });

    // 5. Add environment info
    scope.setTag('env', import.meta.env.VITE_ENV || 'development');
  };

  // Determine fallback component
  const fallbackComponent = showFallback ? fallback : <></>;

  return (
    <SentryErrorBoundary
      fallback={fallbackComponent}
      beforeCapture={beforeCapture}
    >
      {children}
    </SentryErrorBoundary>
  );
};

/**
 * Wrapper that provides location context
 * This allows EnhancedErrorBoundary to work even when used outside Router context
 */
export const EnhancedErrorBoundary = (props) => {
  // Try to use router context, but don't fail if not available
  try {
    return <EnhancedErrorBoundaryInner {...props} />;
  } catch (e) {
    // If router context not available, use basic Sentry ErrorBoundary
    console.warn('EnhancedErrorBoundary used outside Router context, falling back to basic ErrorBoundary');
    return (
      <SentryErrorBoundary fallback={props.fallback || DefaultError}>
        {props.children}
      </SentryErrorBoundary>
    );
  }
};

export default EnhancedErrorBoundary;
